/**
 * BlockRegistry
 *
 * E018-S009: Single source of truth for blocks on canvas.
 * GUARANTEE: Only emits blocks that are IN gfx.layer.
 */

import {
  createDatabaseBlockDataSource,
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
} from '@blocksuite/affine/blocks/database';
import type { Bound } from '@blocksuite/affine/global/gfx';
import type { DatabaseBlockModel as AffineDatabaseBlockModel } from '@blocksuite/affine-model';
import type { GfxController, GfxModel } from '@blocksuite/std/gfx';
import {
  effect as signalEffect,
  type ReadonlySignal,
} from '@preact/signals-core';
import {
  FILE_INDEX_MANAGED_BY,
  isHiddenSystemManagedDatabase,
} from '@streamlain/database-identity';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  type Observable,
  shareReplay,
  type Subscription,
} from 'rxjs';

import type { IBlockRegistry } from '../core/interfaces/IBlockRegistry';
import type {
  BlockRegistryState,
  InputTableRef,
  OutputTable,
  RegisteredAutomation,
  RegisteredDatabase,
  RegisteredElster,
  RegisteredFileSource,
  RegisteredGhost,
  RelationColumnConfig,
} from '../core/types';

// =============================================================================
// Helper Types
// =============================================================================

interface DatabaseBlockModel {
  id: string;
  // Columns can be directly on the model or nested in props
  columns?: DatabaseColumnModel[];
  props?: {
    managedBy?: string;
    visible?: boolean;
    columns?: DatabaseColumnModel[];
  };
}

interface DatabaseColumnModel {
  id: string;
  type: string;
  data?: {
    targetDatabaseId?: string;
    targetDocId?: string;
    targetFieldId?: string;
    cardinality?: 'one' | 'many';
    inverseFieldId?: string;
    inverseCardinality?: 'one' | 'many';
  };
}

type DatabaseColumnsSignal = ReadonlySignal<DatabaseColumnModel[]>;

type MaybeElementBound = {
  elementBound?: unknown;
  model?: unknown;
};

interface AutomationBlockModel {
  id: string;
  props?: {
    sourceTableId?: string;
    sourceDocId?: string; // E018-S014-T005
    // sl-4n0: Multi-Output replaces targetTableId
    outputTables?: Array<{ tableId: string; tableName: string; docId: string }>;
    // sl-m379: Multi-Input tables
    inputTables?: Array<{
      tableId: string;
      role: 'lead' | 'secondary';
      linkedVia?: string;
      docId?: string;
    }>;
  };
}

interface ElsterBlockModel {
  id: string;
  props?: {
    inputTableId?: string;
    inputDocId?: string;
  };
}

interface GhostBlockModel {
  id: string;
  props?: {
    sourceDatabaseId?: string;
    sourceElementId?: string;
    targetDatabaseId?: string;
    targetDocId?: string;
    ghostType?: 'database' | 'automation';
    targetAccessState?: 'write' | 'read' | 'restricted' | 'missing' | 'trashed';
    automationDirection?: 'source' | 'target';
  };
}

// =============================================================================
// Implementation
// =============================================================================

/**
 * Registry that tracks blocks on canvas with guaranteed availability.
 *
 * Key principle: Only emits blocks that are ACTUALLY in gfx.layer.
 * This eliminates race conditions.
 */
export class BlockRegistry implements IBlockRegistry {
  private readonly databasesSubject$ = new BehaviorSubject<
    RegisteredDatabase[]
  >([]);
  private readonly automationsSubject$ = new BehaviorSubject<
    RegisteredAutomation[]
  >([]);
  private readonly elstersSubject$ = new BehaviorSubject<RegisteredElster[]>(
    []
  );
  private readonly fileSourcesSubject$ = new BehaviorSubject<
    RegisteredFileSource[]
  >([]);
  private readonly ghostsSubject$ = new BehaviorSubject<RegisteredGhost[]>([]);

  private layerSubscription: Subscription | null = null;
  private readonly storageReadyDisposers = new Map<string, () => void>();

  readonly databases$: Observable<RegisteredDatabase[]>;
  readonly automations$: Observable<RegisteredAutomation[]>;
  readonly elsters$: Observable<RegisteredElster[]>;
  readonly fileSources$: Observable<RegisteredFileSource[]>;
  readonly ghosts$: Observable<RegisteredGhost[]>;
  readonly allBlocks$: Observable<BlockRegistryState>;

  constructor(private readonly gfx: GfxController) {
    // Create observables with debounce to wait for layer sync
    // CRITICAL: databases$ must use databasesEqual to compare relations!
    this.databases$ = this.databasesSubject$.pipe(
      distinctUntilChanged((a, b) => this.databasesEqual(a, b)),
      shareReplay(1)
    );

    // CRITICAL: automations$ must use automationsEqual to compare table IDs!
    this.automations$ = this.automationsSubject$.pipe(
      distinctUntilChanged((a, b) => this.automationsEqual(a, b)),
      shareReplay(1)
    );

    // ELSTER blocks: compare inputTableId
    this.elsters$ = this.elstersSubject$.pipe(
      distinctUntilChanged((a, b) => this.elstersEqual(a, b)),
      shareReplay(1)
    );

    this.fileSources$ = this.fileSourcesSubject$.pipe(
      distinctUntilChanged((a, b) => this.fileSourcesEqual(a, b)),
      shareReplay(1)
    );

    this.ghosts$ = this.ghostsSubject$.pipe(
      distinctUntilChanged(this.arraysEqual),
      shareReplay(1)
    );

    this.allBlocks$ = combineLatest([
      this.databases$,
      this.automations$,
      this.elsters$,
      this.fileSources$,
      this.ghosts$,
    ]).pipe(
      map(([databases, automations, elsters, fileSources, ghosts]) => ({
        databases,
        automations,
        elsters,
        fileSources,
        ghosts,
      })),
      shareReplay(1)
    );

    this.setupLayerSubscription();
  }

  /**
   * Sets up subscription to layer updates.
   */
  private setupLayerSubscription(): void {
    // Initial scan
    this.scanLayer();

    // Subscribe to layer updates with debounce
    this.layerSubscription = this.gfx.layer.slots.layerUpdated
      .pipe(
        // Batch rapid updates (e.g. template insert) into single scan
        debounceTime(8)
      )
      .subscribe(() => {
        this.scanLayer();
      });
  }

  /**
   * Scans the layer and updates all subjects.
   */
  private scanLayer(): void {
    const databases: RegisteredDatabase[] = [];
    const automations: RegisteredAutomation[] = [];
    const elsters: RegisteredElster[] = [];
    const fileSources: RegisteredFileSource[] = [];
    const ghosts: RegisteredGhost[] = [];

    for (const block of this.gfx.layer.blocks) {
      const flavour = (block as { flavour?: string }).flavour;

      switch (flavour) {
        case 'affine:database':
          {
            const db = this.mapDatabase(block);
            if (db) databases.push(db);
          }
          break;

        case 'affine:automation':
          {
            const auto = this.mapAutomation(block);
            if (auto) automations.push(auto);
          }
          break;

        case 'affine:elster':
          {
            const elster = this.mapElster(block);
            if (elster) elsters.push(elster);
          }
          break;

        case 'affine:attachment':
        case 'affine:image':
          {
            const fileSource = this.mapFileSource(block);
            if (fileSource) this.addFileSource(fileSources, fileSource);
          }
          break;

        case 'affine:ghost':
          {
            const ghost = this.mapGhost(block);
            if (ghost) ghosts.push(ghost);
          }
          break;

        case 'affine:note':
          // Check for embedded databases in notes
          {
            const embeddedDbs = this.extractEmbeddedDatabases(block);
            databases.push(...embeddedDbs);
            const embeddedFileSources = this.extractEmbeddedFileSources(block);
            for (const fileSource of embeddedFileSources) {
              this.addFileSource(fileSources, fileSource);
            }
          }
          break;
      }
    }

    this.databasesSubject$.next(databases);
    this.automationsSubject$.next(automations);
    this.elstersSubject$.next(elsters);
    this.fileSourcesSubject$.next(fileSources);
    this.ghostsSubject$.next(ghosts);
  }

  /**
   * Maps a database block to RegisteredDatabase.
   */
  private mapDatabase(block: GfxModel): RegisteredDatabase | null {
    const bound = this.getBound(block);
    if (!bound) return null;

    const model = this.getBlockModel<DatabaseBlockModel>(block);
    const relations = this.extractRelations(model as AffineDatabaseBlockModel);

    const managedBy =
      typeof model.props?.managedBy === 'string'
        ? model.props.managedBy
        : undefined;
    if (
      isHiddenSystemManagedDatabase({
        id: block.id,
        managedBy,
        visible: model.props?.visible,
      })
    ) {
      return null;
    }

    return {
      blockId: block.id,
      elementId: block.id,
      bound,
      ...(managedBy ? { managedBy } : {}),
      ...(managedBy === FILE_INDEX_MANAGED_BY
        ? { visible: model.props?.visible === true }
        : {}),
      relations,
    };
  }

  /**
   * Maps an automation block to RegisteredAutomation.
   */
  private mapAutomation(block: GfxModel): RegisteredAutomation | null {
    const bound = this.getBound(block);
    if (!bound) return null;

    const model = this.getBlockModel<AutomationBlockModel>(block);
    const props = model.props;

    return {
      blockId: block.id,
      elementId: block.id,
      bound,
      sourceTableId: props?.sourceTableId ?? null,
      sourceDocId: props?.sourceDocId ?? null, // E018-S014-T005
      outputTables: this.snapshotOutputTables(props),
      inputTables: this.snapshotInputTables(props),
    };
  }

  private snapshotOutputTables(
    props: AutomationBlockModel['props']
  ): OutputTable[] {
    return (props?.outputTables ?? []).map(table => ({
      tableId: table.tableId,
      tableName: table.tableName,
      docId: table.docId,
    }));
  }

  private snapshotInputTables(
    props: AutomationBlockModel['props']
  ): InputTableRef[] {
    return (props?.inputTables ?? []).map(table => ({
      tableId: table.tableId,
      role: table.role,
      ...(typeof table.linkedVia === 'string'
        ? { linkedVia: table.linkedVia }
        : {}),
      ...(typeof table.docId === 'string' ? { docId: table.docId } : {}),
    }));
  }

  /**
   * Maps an ELSTER block to RegisteredElster.
   */
  private mapElster(block: GfxModel): RegisteredElster | null {
    const bound = this.getBound(block);
    if (!bound) return null;

    const model = this.getBlockModel<ElsterBlockModel>(block);
    const props = model.props;

    return {
      blockId: block.id,
      elementId: block.id,
      bound,
      inputTableId: props?.inputTableId ?? null,
      inputDocId: props?.inputDocId ?? null,
    };
  }

  /**
   * Maps a ghost block to RegisteredGhost.
   */
  private mapGhost(block: GfxModel): RegisteredGhost | null {
    const bound = this.getBound(block);
    if (!bound) return null;

    const model = this.getBlockModel<GhostBlockModel>(block);
    const props = model.props;
    if (!props?.targetDatabaseId) return null;

    return {
      blockId: block.id,
      elementId: block.id,
      bound,
      targetId: props.targetDatabaseId,
      ghostType: props.ghostType ?? 'database',
      sourceBlockId: props.sourceDatabaseId ?? '',
      targetDocId: props.targetDocId ?? '',
      targetAccessState: props.targetAccessState,
      automationDirection: props.automationDirection,
    };
  }

  /**
   * Maps an image/attachment block to a File Index source endpoint.
   */
  private mapFileSource(block: GfxModel): RegisteredFileSource | null {
    const bound = this.getBound(block);
    if (!bound) return null;

    return {
      blockId: block.id,
      elementId: block.id,
      bound,
    };
  }

  private addFileSource(
    fileSources: RegisteredFileSource[],
    fileSource: RegisteredFileSource
  ): void {
    const existingIndex = fileSources.findIndex(
      source => source.blockId === fileSource.blockId
    );
    if (existingIndex < 0) {
      fileSources.push(fileSource);
      return;
    }

    // Prefer a directly visible file block over a Note proxy if both are
    // present during a transient layer update.
    if (fileSource.elementId === fileSource.blockId) {
      fileSources[existingIndex] = fileSource;
    }
  }

  private getBlockModel<T extends { id: string; props?: unknown }>(
    block: GfxModel
  ): T {
    return ((block as { model?: T }).model ?? block) as unknown as T;
  }

  private getBlockChildren(block: GfxModel): GfxModel[] {
    return ((block as { children?: GfxModel[] }).children ?? []).filter(
      child => child && typeof child === 'object'
    );
  }

  private isFileSourceFlavour(flavour: unknown): boolean {
    return flavour === 'affine:attachment' || flavour === 'affine:image';
  }

  /**
   * Extracts databases embedded in notes.
   */
  private extractEmbeddedDatabases(noteBlock: GfxModel): RegisteredDatabase[] {
    const results: RegisteredDatabase[] = [];

    const children = this.getBlockChildren(noteBlock);
    if (!children) return results;

    for (const child of children) {
      const flavour = (child as { flavour?: string }).flavour;
      if (flavour === 'affine:database') {
        const model = (child as { model?: DatabaseBlockModel }).model ?? child;
        const relations = this.extractRelations(
          model as AffineDatabaseBlockModel
        );

        // Use note's bound for embedded databases
        const bound = this.getBound(noteBlock);
        if (bound) {
          const managedBy =
            typeof (model as { props?: { managedBy?: unknown } }).props
              ?.managedBy === 'string'
              ? (model as { props: { managedBy: string } }).props.managedBy
              : undefined;
          const visible = (model as { props?: { visible?: unknown } }).props
            ?.visible;
          if (
            isHiddenSystemManagedDatabase({
              id: child.id,
              managedBy,
              visible,
            })
          ) {
            continue;
          }
          results.push({
            blockId: child.id,
            elementId: noteBlock.id, // Use note's element ID!
            bound,
            ...(managedBy ? { managedBy } : {}),
            ...(managedBy === FILE_INDEX_MANAGED_BY
              ? { visible: visible === true }
              : {}),
            relations,
          });
        }
      }
    }

    return results;
  }

  /**
   * Extracts file sources embedded in notes.
   *
   * The file block remains the semantic source (`blockId`), but the Note is the
   * visible canvas endpoint (`elementId`) because nested file blocks do not
   * have their own canvas element in gfx.layer.
   */
  private extractEmbeddedFileSources(
    noteBlock: GfxModel
  ): RegisteredFileSource[] {
    const bound = this.getBound(noteBlock);
    if (!bound) return [];

    const results: RegisteredFileSource[] = [];
    const visit = (block: GfxModel) => {
      const flavour = (block as { flavour?: string }).flavour;
      if (this.isFileSourceFlavour(flavour)) {
        results.push({
          blockId: block.id,
          elementId: noteBlock.id,
          bound,
        });
        return;
      }

      for (const child of this.getBlockChildren(block)) {
        visit(child);
      }
    };

    for (const child of this.getBlockChildren(noteBlock)) {
      visit(child);
    }

    return results;
  }

  /**
   * Extracts relation columns from the database storage model.
   * PageDoc database blocks are references: schema lives in the DatabaseRootDoc,
   * so reading only the reference block misses sharded/site database relations.
   */
  private extractRelations(
    hostModel: AffineDatabaseBlockModel
  ): RelationColumnConfig[] {
    this.observeStorageModelReady(hostModel);
    const model = this.resolveStorageModel(hostModel);
    // Check both locations where columns might be stored
    const columns = model.columns || model.props?.columns || [];
    const relations: RelationColumnConfig[] = [];

    for (const column of columns) {
      if (column.type === 'relation' && column.data?.targetDatabaseId) {
        relations.push({
          columnId: column.id,
          targetDatabaseId: column.data.targetDatabaseId,
          targetDocId: column.data.targetDocId,
          targetFieldId: column.data.targetFieldId ?? column.id,
          cardinality: column.data.cardinality ?? 'many',
          inverseFieldId: column.data.inverseFieldId,
          inverseCardinality: column.data.inverseCardinality,
        });
      }
    }

    return relations;
  }

  private resolveStorageModel(
    hostModel: AffineDatabaseBlockModel
  ): DatabaseBlockModel {
    try {
      return createDatabaseBlockDataSource(hostModel)
        .storageModel as DatabaseBlockModel;
    } catch {
      return hostModel as unknown as DatabaseBlockModel;
    }
  }

  private observeStorageModelReady(hostModel: AffineDatabaseBlockModel): void {
    const blockId = hostModel.id;
    if (this.storageReadyDisposers.has(blockId)) return;

    try {
      if (isDatabaseStorageModelReady(hostModel)) {
        this.observeStorageModelColumns(hostModel);
        return;
      }
      const dispose = observeDatabaseStorageModelReady(hostModel, () => {
        queueMicrotask(() => {
          this.storageReadyDisposers.get(blockId)?.();
          this.storageReadyDisposers.delete(blockId);
          this.forceRescan();
        });
      });
      this.storageReadyDisposers.set(blockId, dispose);
    } catch {
      // If storage resolution is unavailable, keep the registry functional
      // with the host block props and let normal block/layer updates rescan.
    }
  }

  private observeStorageModelColumns(
    hostModel: AffineDatabaseBlockModel
  ): void {
    const blockId = hostModel.id;
    const storageModel = this.resolveStorageModel(hostModel);
    const storageProps = storageModel.props as
      | { [key: string]: DatabaseColumnsSignal | undefined }
      | undefined;
    const columnsSignal = storageProps?.['columns$'];
    if (!columnsSignal) return;

    let initialized = false;
    const dispose = signalEffect(() => {
      void columnsSignal.value;
      if (!initialized) {
        initialized = true;
        return;
      }
      queueMicrotask(() => this.forceRescan());
    });
    this.storageReadyDisposers.set(blockId, dispose);
  }

  /**
   * Gets bound from a block, ensuring it's valid.
   */
  private getBound(block: GfxModel): Bound | null {
    return (
      this.extractBound(block) ??
      this.extractBound((block as MaybeElementBound).model)
    );
  }

  private extractBound(source: unknown): Bound | null {
    if (!source || typeof source !== 'object' || !('elementBound' in source)) {
      return null;
    }

    const bound = (source as MaybeElementBound).elementBound;
    if (!this.isFiniteBound(bound)) return null;

    return bound;
  }

  private isFiniteBound(bound: unknown): bound is Bound {
    if (!bound || typeof bound !== 'object') return false;

    const candidate = bound as Partial<Bound>;
    return (
      Number.isFinite(candidate.x) &&
      Number.isFinite(candidate.y) &&
      Number.isFinite(candidate.w) &&
      Number.isFinite(candidate.h)
    );
  }

  /**
   * Compares arrays for equality (shallow by blockId).
   */
  private arraysEqual<T extends { blockId: string }>(a: T[], b: T[]): boolean {
    if (a.length !== b.length) return false;
    const aIds = new Set(a.map(x => x.blockId));
    const bIds = new Set(b.map(x => x.blockId));
    if (aIds.size !== bIds.size) return false;
    for (const id of aIds) {
      if (!bIds.has(id)) return false;
    }
    return true;
  }

  /**
   * Compares database arrays including relations and elementId.
   * CRITICAL: Must compare relations AND elementId!
   * - relations: Otherwise new relations won't trigger updates.
   * - elementId: Otherwise moving DB to/from Note won't trigger edge updates.
   */
  private databasesEqual(
    a: RegisteredDatabase[],
    b: RegisteredDatabase[]
  ): boolean {
    if (a.length !== b.length) return false;

    // Create maps for efficient lookup
    const aMap = new Map(a.map(db => [db.blockId, db]));
    const bMap = new Map(b.map(db => [db.blockId, db]));

    // Check all keys match
    if (aMap.size !== bMap.size) return false;
    for (const key of aMap.keys()) {
      if (!bMap.has(key)) return false;
    }

    // Compare elementId and relations for each database
    for (const [blockId, aDb] of aMap) {
      const bDb = bMap.get(blockId)!;

      // Compare elementId (changes when DB moves to/from Note)
      if (aDb.elementId !== bDb.elementId) return false;

      // Compare relation count
      if (aDb.relations.length !== bDb.relations.length) return false;

      // Compare relation targets (order doesn't matter)
      const aTargets = new Set(aDb.relations.map(r => r.targetDatabaseId));
      const bTargets = new Set(bDb.relations.map(r => r.targetDatabaseId));
      if (aTargets.size !== bTargets.size) return false;
      for (const target of aTargets) {
        if (!bTargets.has(target)) return false;
      }
    }

    return true;
  }

  /**
   * Compares automation arrays including sourceTableId/targetTableId.
   * CRITICAL: Must compare table IDs too, not just blockIds!
   * Otherwise new automation configurations won't trigger ghost creation.
   */
  private automationsEqual(
    a: RegisteredAutomation[],
    b: RegisteredAutomation[]
  ): boolean {
    if (a.length !== b.length) return false;

    // Create maps for efficient lookup
    const aMap = new Map(a.map(auto => [auto.blockId, auto]));
    const bMap = new Map(b.map(auto => [auto.blockId, auto]));

    // Check all keys match
    if (aMap.size !== bMap.size) return false;
    for (const key of aMap.keys()) {
      if (!bMap.has(key)) return false;
    }

    // Compare sourceTableId and outputTables for each automation
    for (const [blockId, aAuto] of aMap) {
      const bAuto = bMap.get(blockId)!;

      // Compare source table ID
      if (aAuto.sourceTableId !== bAuto.sourceTableId) return false;
      if (aAuto.sourceDocId !== bAuto.sourceDocId) return false;

      // sl-4n0: Compare outputTables arrays
      const aOutputs = aAuto.outputTables ?? [];
      const bOutputs = bAuto.outputTables ?? [];
      if (aOutputs.length !== bOutputs.length) return false;
      for (let i = 0; i < aOutputs.length; i++) {
        if (aOutputs[i].tableId !== bOutputs[i].tableId) return false;
        if (aOutputs[i].docId !== bOutputs[i].docId) return false;
      }

      // sl-m379: Compare inputTables arrays
      const aInputs = aAuto.inputTables ?? [];
      const bInputs = bAuto.inputTables ?? [];
      if (aInputs.length !== bInputs.length) return false;
      for (let i = 0; i < aInputs.length; i++) {
        if (aInputs[i].tableId !== bInputs[i].tableId) return false;
        if (aInputs[i].role !== bInputs[i].role) return false;
      }
    }

    return true;
  }

  /**
   * Compares ELSTER arrays including inputTableId.
   */
  private elstersEqual(a: RegisteredElster[], b: RegisteredElster[]): boolean {
    if (a.length !== b.length) return false;

    const aMap = new Map(a.map(e => [e.blockId, e]));
    const bMap = new Map(b.map(e => [e.blockId, e]));

    if (aMap.size !== bMap.size) return false;
    for (const key of aMap.keys()) {
      if (!bMap.has(key)) return false;
    }

    for (const [blockId, aElster] of aMap) {
      const bElster = bMap.get(blockId)!;
      if (aElster.inputTableId !== bElster.inputTableId) return false;
      if (aElster.inputDocId !== bElster.inputDocId) return false;
    }

    return true;
  }

  /**
   * Compares file sources including elementId.
   * Moving a file into/out of a Note keeps blockId stable but changes the
   * visible edge endpoint.
   */
  private fileSourcesEqual(
    a: RegisteredFileSource[],
    b: RegisteredFileSource[]
  ): boolean {
    if (a.length !== b.length) return false;

    const aMap = new Map(a.map(source => [source.blockId, source]));
    const bMap = new Map(b.map(source => [source.blockId, source]));
    if (aMap.size !== bMap.size) return false;
    for (const key of aMap.keys()) {
      if (!bMap.has(key)) return false;
    }

    for (const [blockId, aSource] of aMap) {
      const bSource = bMap.get(blockId)!;
      if (aSource.elementId !== bSource.elementId) return false;
    }

    return true;
  }

  // ===========================================================================
  // Public API
  // ===========================================================================

  getDatabases(): RegisteredDatabase[] {
    return this.databasesSubject$.value;
  }

  getAutomations(): RegisteredAutomation[] {
    return this.automationsSubject$.value;
  }

  getElsters(): RegisteredElster[] {
    return this.elstersSubject$.value;
  }

  getFileSources(): RegisteredFileSource[] {
    return this.fileSourcesSubject$.value;
  }

  getGhosts(): RegisteredGhost[] {
    return this.ghostsSubject$.value;
  }

  findDatabase(blockId: string): RegisteredDatabase | null {
    return (
      this.databasesSubject$.value.find(db => db.blockId === blockId) ?? null
    );
  }

  findAutomation(blockId: string): RegisteredAutomation | null {
    return (
      this.automationsSubject$.value.find(auto => auto.blockId === blockId) ??
      null
    );
  }

  findElster(blockId: string): RegisteredElster | null {
    return this.elstersSubject$.value.find(e => e.blockId === blockId) ?? null;
  }

  findGhostByTarget(targetId: string): RegisteredGhost | null {
    return (
      this.ghostsSubject$.value.find(ghost => ghost.targetId === targetId) ??
      null
    );
  }

  /**
   * Forces a rescan of the layer.
   * Called when block properties change (like adding a relation column).
   * Layer updates don't fire for property changes - only for add/remove/move.
   */
  forceRescan(): void {
    this.scanLayer();
  }

  dispose(): void {
    this.layerSubscription?.unsubscribe();
    this.layerSubscription = null;
    this.storageReadyDisposers.forEach(dispose => dispose());
    this.storageReadyDisposers.clear();
    this.databasesSubject$.complete();
    this.automationsSubject$.complete();
    this.elstersSubject$.complete();
    this.fileSourcesSubject$.complete();
    this.ghostsSubject$.complete();
  }
}
